/** @jsxImportSource @emotion/react */
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  SxProps,
  TextField,
  Typography
} from '@mui/material';
import { SandboxPaper } from 'src/pages/MuiSandboxPage/SandboxPaper';
import { TwoColumnPageBase } from 'src/components/PageBase/TwoColumnPageBase';
import { useAppDispatch } from 'src/app/hooks';
import { showSnackBar } from 'src/api/ui-slice';
import { theme } from 'src/theme/mui-theme';

/**
 * example for css composition using sxprops. See the usage below.
 */
const defaultPaddingStyle = {
  padding: '1rem'
} as SxProps

export function MuiSandboxPage() {

  const dispatch = useAppDispatch();

  function onDisplaySnackbarClick() {
    dispatch(showSnackBar({ text: 'You clicked on display snack bar', severity: 'info'}));
  }

  return (
      /* Example for using TwoColumnPageBase */
      <TwoColumnPageBase
          leftContent={
            <>
              <List>
                <ListItem disablePadding>
                  <ListItemButton>
                    <ListItemText primary="Menu 1"/>
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton>
                    <ListItemText primary="Menu 2"/>
                  </ListItemButton>
                </ListItem>
              </List>
            </>}
          pageContent={
            <>
              <SandboxPaper>
                <Typography variant="h6">Buttons</Typography>
                <Stack direction='row' spacing='0.5rem'>
                  {/* example for setting colors from theme */}
                  <Button variant='contained' color='primary'>Contained Primary Color</Button>
                  <Button variant='contained' color='secondary'>Contained Secondary Color</Button>
                  <Button variant='outlined'>Outlined</Button>
                  {/* example for setting custom color from theme. See mui-theme.ts for the definition */}
                  <Button variant='contained' color='custom1'>Custom color</Button>
                  {/* example for using snack bar*/}
                  <Button variant='contained' color='primary' onClick={onDisplaySnackbarClick}>Display snack bar</Button>
                </Stack>
              </SandboxPaper>
              <SandboxPaper>
                {/* example for setting color from palette with sx */}
                <Box sx={{backgroundColor: theme.palette.primary.main, width: '100px'}}>
                   Colored box
                </Box>
              </SandboxPaper>
              <SandboxPaper>
                <Typography variant="h6">Form</Typography>
                <Stack direction='column' spacing='0.5rem'>
                  <TextField
                      required
                      id="outlined-required"
                      label="Required"
                      defaultValue="Hello World"
                      sx={{width: '200px'}}
                  />
                  <TextField
                      id="outlined-helperText"
                      label="Helper text"
                      defaultValue="Default Value"
                      helperText="Some important text"
                  />
                  <FormControl>
                    <InputLabel id="demo-simple-select-label">Age</InputLabel>
                    <Select
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        value={10}
                        label="Age"
                    >
                      <MenuItem value={10}>Ten</MenuItem>
                      <MenuItem value={20}>Twenty</MenuItem>
                      <MenuItem value={30}>Thirty</MenuItem>
                    </Select>
                  </FormControl>
                  <Stack direction='row'>
                    <Button variant='contained' color='primary'>Save</Button>
                  </Stack>
                </Stack>
              </SandboxPaper>
              <SandboxPaper>
                <Box
                    /* example for style composition by reusing styles */
                    sx={{
                      ...defaultPaddingStyle,
                      width: '100%',
                      maxWidth: 600
                    }}>
                  <Typography variant="h1" gutterBottom>
                    h1. Heading
                  </Typography>
                  <Typography variant="h2" gutterBottom>
                    h2. Heading
                  </Typography>
                  <Typography variant="h3" gutterBottom>
                    h3. Heading
                  </Typography>
                  <Typography variant="h4" gutterBottom>
                    h4. Heading
                  </Typography>
                  <Typography variant="h5" gutterBottom>
                    h5. Heading
                  </Typography>
                  <Typography variant="h6" gutterBottom>
                    h6. Heading
                  </Typography>
                  <Typography variant="subtitle1" gutterBottom>
                    subtitle1. Lorem ipsum dolor sit amet, consectetur adipisicing elit. Quos
                    blanditiis tenetur
                  </Typography>
                  <Typography variant="subtitle2" gutterBottom>
                    subtitle2. Lorem ipsum dolor sit amet, consectetur adipisicing elit. Quos
                    blanditiis tenetur
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    body1. Lorem ipsum dolor sit amet, consectetur adipisicing elit. Quos
                    blanditiis tenetur unde suscipit, quam beatae rerum inventore consectetur,
                    neque doloribus, cupiditate numquam dignissimos laborum fugiat deleniti? Eum
                    quasi quidem quibusdam.
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    body2. Lorem ipsum dolor sit amet, consectetur adipisicing elit. Quos
                    blanditiis tenetur unde suscipit, quam beatae rerum inventore consectetur,
                    neque doloribus, cupiditate numquam dignissimos laborum fugiat deleniti? Eum
                    quasi quidem quibusdam.
                  </Typography>
                  <Typography variant="button" gutterBottom sx={{display: 'block'}}>
                    button text
                  </Typography>
                  <Typography variant="caption" gutterBottom sx={{display: 'block'}}>
                    caption text
                  </Typography>
                  <Typography variant="overline" gutterBottom sx={{display: 'block'}}>
                    overline text
                  </Typography>
                </Box>
              </SandboxPaper>
            </>
          }
      />
  );
}